import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import ToolFooter from '../../components/ui/ToolFooter';
import AuthorCredit from '../../components/ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../../components/ui/FreshnessDate';

export default function JointPropertyChecker() {
  const [rent, setRent] = useState(60000);
  const [share, setShare] = useState(50);
  const [otherIncome, setOtherIncome] = useState(0);

  const qualifyingIncome = (rent * (share / 100)) + otherIncome;

  let result = {
    status: "NOT MANDATED",
    message: "Based on these figures, you fall below the initial thresholds for MTD.",
    date: "N/A",
    color: "emerald"
  };

  if (qualifyingIncome > 50000) {
    result = {
      status: "MANDATED",
      message: "Your total qualifying income exceeds £50,000.",
      date: "From 6 April 2026",
      color: "red"
    };
  } else if (qualifyingIncome > 30000) {
    result = {
      status: "MANDATED FROM APRIL 2027",
      message: "Your qualifying income is between £30k and £50k.",
      date: "From 6 April 2027",
      color: "amber"
    };
  }

  // Calculate position for the risk meter indicator
  // Max scale is £60k for visual purposes
  const meterPercent = Math.min(Math.max((qualifyingIncome / 60000) * 100, 0), 100);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [{
      "@type": "Question",
      "name": "How do joint property owners report MTD?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For Making Tax Digital (MTD), your qualifying income is based on your exact share of the jointly owned property's gross income, plus any other self-employment income you have."
      }
    }]
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Automate Joint Property Split Expenses for MTD",
    "dateModified": getFreshnessDateIso(),
    "step": [
      {"@type": "HowToStep", "text": "Take a photo of a joint property repair receipt."},
      {"@type": "HowToStep", "text": "Send it to Zap Expenses via WhatsApp."},
      {"@type": "HowToStep", "text": "The tool automatically detects your pre-set split ratio (e.g. 50%) and claims only that fraction against your individual MTD record."}
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-indigo-500/30">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify([faqSchema, howToSchema])}</script>
        <title>Joint Property &amp; Shared Income Checker | Zap Expenses</title>
      </Helmet>

      <div className="max-w-2xl mx-auto py-16 px-6">

        <FreshnessDate className="mb-4" />
        
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
          Do I qualify for MTD if I <span className="text-indigo-400">share my rental income?</span>
        </h1>
        
        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>Making Tax Digital thresholds apply per individual taxpayer, not per property. If you own 50% of a rental, only exactly half of its gross revenue counts towards your personal £50,000 allowance.</strong>
          </p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-xl text-left text-sm text-slate-300 w-full mb-12">
            <strong className="text-emerald-400 block mb-1">Audience Context:</strong>
            "40.6% of UK unincorporated landlords (1.17 million people) declare residential finance costs, a key MTD reporting field."
            <div className="text-[10px] text-slate-500 italic mt-2 border-t border-emerald-500/20 pt-2">
                Source: <a href="https://www.gov.uk/government/statistics/property-rental-income-statistics/property-rental-income-statistics-2024" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400">HMRC, Property Rental Statistics 2024</a>
            </div>
        </div>

        <div className="prose prose-invert max-w-none text-slate-300 mb-12">
          <p>
            When landlords fail to formally split gross revenue and expense deductions accurately, HMRC frequently over-calculates their mandate status. Calculate your exact Making Tax Digital liability below by isolating your true share of joint property income and adding your solitary sole trader revenue.
          </p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl mb-8">
          <div className="space-y-8">
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                1. Total Gross Annual Rental Income of Property
              </label>
              <p className="text-xs text-slate-500 mb-3">The total amount generated by the property, before expenses.</p>
              <div className="relative max-w-sm">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">£</span>
                <input
                  type="number"
                  value={rent}
                  onChange={(e) => setRent(Number(e.target.value))}
                  className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-8 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                2. Your Ownership Share
              </label>
              <div className="flex items-center gap-4 max-w-sm">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={share}
                  onChange={(e) => setShare(Number(e.target.value))}
                  className="flex-1 accent-indigo-500"
                />
                <span className="font-mono font-bold text-lg w-16 text-right">{share}%</span>
              </div>
              <p className="text-sm font-mono text-indigo-400 mt-2">
                Your share: £{(rent * (share / 100)).toLocaleString()}
              </p>
            </div>

            <hr className="border-white/5" />

            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                3. Other Sole Trader / Self-Employment Income
              </label>
              <p className="text-xs text-slate-500 mb-3">Any other gross income you earn as an individual.</p>
              <div className="relative max-w-sm">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">£</span>
                <input
                  type="number"
                  value={otherIncome}
                  onChange={(e) => setOtherIncome(Number(e.target.value))}
                  className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-8 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Risk Meter & Result */}
        <div className={`p-8 rounded-2xl border transition-all duration-500 ${
          result.color === 'red' ? 'bg-red-500/10 border-red-500/30' :
          result.color === 'amber' ? 'bg-amber-500/10 border-amber-500/30' :
          'bg-emerald-500/10 border-emerald-500/30'
        }`}>
          <div className="text-center mb-6">
            <h3 className={`text-4xl font-extrabold mb-2 ${
              result.color === 'red' ? 'text-red-400' :
              result.color === 'amber' ? 'text-amber-400' :
              'text-emerald-400'
            }`}>
              {result.status}
            </h3>
            <p className="text-white text-xl font-bold">{result.date}</p>
            <p className="text-slate-300 mt-2">{result.message}</p>
          </div>

          <div className="max-w-lg mx-auto">
            <div className="flex justify-between text-xs font-bold text-slate-500 mb-2 font-mono uppercase">
              <span>£0</span>
              <span>£30k</span>
              <span>£50k</span>
            </div>
            <div className="h-4 w-full bg-black rounded-full overflow-hidden flex relative">
              <div className="h-full bg-emerald-500" style={{ width: '50%' }}></div>
              <div className="h-full bg-amber-500" style={{ width: '33.33%' }}></div>
              <div className="h-full bg-red-500" style={{ width: '16.67%' }}></div>
              
              {/* Indicator */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_white] transition-all duration-500 ease-out z-10"
                style={{ left: `${meterPercent}%` }}
              ></div>
            </div>
            <div className="text-center mt-4">
              <span className="text-sm text-slate-400 uppercase tracking-widest font-semibold mr-2">Your Qualifying Income:</span>
              <span className="text-2xl font-mono font-bold text-white">£{qualifyingIncome.toLocaleString()}</span>
            </div>
          </div>
        </div>



        <div className="mt-6 mb-2 text-center">
          <a href="https://www.gov.uk/guidance/income-tax-when-you-rent-out-a-property-working-out-your-rental-income" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
            Review HMRC guidelines on Working Out Rental Income →
          </a>
        </div>

        <div className="mt-8">
          <ToolFooter />
        </div>
      </div>
    </div>
  );
}
